import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, FileText, ShieldCheck } from 'lucide-react';
import { SITE } from '../mock';

// Google Form URL — application form shown after Razorpay payment success
const GOOGLE_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSdRcD2v-QKaRisxwFLmLD4QjqTOiplYMaZpn_pGj_tE0epU6Q/viewform?usp=dialog';

// Seconds to wait before auto-redirecting to the Google Form (only if URL set)
const AUTO_REDIRECT_SECONDS = 8;

export default function ThankYouPage() {
  const [seconds, setSeconds] = useState(AUTO_REDIRECT_SECONDS);
  const hasForm = GOOGLE_FORM_URL && GOOGLE_FORM_URL.startsWith('http');

  useEffect(() => {
    if (!hasForm) return undefined;
    if (seconds <= 0) {
      window.location.href = GOOGLE_FORM_URL;
      return undefined;
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds, hasForm]);

  return (
    <div className="min-h-screen bg-navy-950 text-white">
      {/* Ambient glow */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1100px] h-[700px] rounded-full bg-amber-300/[0.08] blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-amber-500/[0.05] blur-3xl" />
      </div>

      <main className="relative max-w-3xl mx-auto px-6 pt-20 md:pt-28 pb-24 text-center">
        {/* Success badge */}
        <div className="relative mx-auto w-20 h-20 md:w-24 md:h-24">
          <div className="absolute -inset-6 bg-amber-300/15 blur-2xl rounded-full" />
          <div className="relative w-full h-full rounded-full border border-amber-300/40 bg-gradient-to-b from-amber-300/20 to-amber-500/10 flex items-center justify-center">
            <CheckCircle2 size={44} strokeWidth={1.5} className="text-amber-300" />
          </div>
        </div>

        <p className="mt-6 text-[11px] tracking-[0.28em] uppercase bg-gradient-to-b from-amber-100 to-amber-400 bg-clip-text text-transparent font-semibold">
          Payment Received
        </p>

        <h1 className="mt-4 font-serif text-white text-[36px] md:text-[56px] leading-[1.05] tracking-tight">
          <span className="block bg-gradient-to-b from-white to-neutral-400 bg-clip-text text-transparent">
            Thank You.
          </span>
          <span className="block bg-gradient-to-b from-white to-neutral-400 bg-clip-text text-transparent">
            Your Slot Is <span className="italic text-amber-200">Reserved.</span>
          </span>
        </h1>

        <p className="mt-6 max-w-xl mx-auto text-neutral-400 text-[15px] md:text-[17px] leading-relaxed">
          Your registration payment has been received and your slot in the next 1-to-1 cohort is
          confirmed. One last step before our call — please complete the application form below so
          we understand your story.
        </p>

        {/* What's next card */}
        <div className="mt-12 mx-auto max-w-xl rounded-2xl border border-white/10 bg-navy-900/60 backdrop-blur p-7 md:p-9 text-left">
          <p className="text-[11px] tracking-[0.22em] uppercase text-amber-300/90">
            Final Step
          </p>
          <h2 className="mt-2 font-serif text-white text-[26px] md:text-[32px] tracking-tight">
            Complete Your Application
          </h2>
          <p className="mt-3 text-neutral-400 text-[14px] md:text-[15px] leading-relaxed">
            Fill the detailed application form so we know your goals, current situation, and where
            you want to be 90 days from now. Takes about 5 minutes.
          </p>

          {hasForm ? (
            <>
              <a
                href={GOOGLE_FORM_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-7 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[15px] font-semibold text-navy-900 bg-gradient-to-b from-amber-200 to-amber-400 shadow-[0_10px_36px_-6px_rgba(251,191,36,0.55)] hover:-translate-y-[1px] hover:shadow-[0_16px_44px_-6px_rgba(251,191,36,0.75)] transition-all"
              >
                <FileText size={16} /> Open Application Form
                <ArrowRight size={14} />
              </a>
              <p className="mt-4 text-[12px] tracking-[0.18em] uppercase text-neutral-500">
                Auto-redirecting in {seconds}s&hellip;
              </p>
            </>
          ) : (
            <div className="mt-7">
              <span className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-medium text-neutral-300 border border-white/15 cursor-not-allowed opacity-80">
                <FileText size={16} /> Application Form Coming Shortly
              </span>
              <p className="mt-4 text-[12px] tracking-[0.18em] uppercase text-neutral-500">
                You'll receive the form link via WhatsApp / email within 24 hours.
              </p>
            </div>
          )}
        </div>

        {/* What happens next strip */}
        <div className="mt-12 grid sm:grid-cols-3 gap-3 text-left">
          <InfoCard
            icon={ShieldCheck}
            title="Payment Confirmed"
            body="Your registration is locked in for the upcoming cohort."
          />
          <InfoCard
            icon={FileText}
            title="Application Review"
            body="We review your form within 24–48 hours of submission."
          />
          <InfoCard
            icon={CheckCircle2}
            title="Onboarding Call"
            body="Once approved, we schedule your kickoff 1-to-1 session."
          />
        </div>

        {/* Contact */}
        <p className="mt-14 text-neutral-400 text-sm">
          Any questions?{' '}
          <a className="text-amber-300 hover:underline underline-offset-4" href="mailto:info@nishitthakkar.in">
            info@nishitthakkar.in
          </a>
        </p>

        <div className="mt-10">
          <Link
            to="/"
            className="text-[12px] tracking-[0.2em] uppercase text-neutral-500 hover:text-amber-300 transition-colors"
          >
            ← Back to home
          </Link>
        </div>

        {/* Footer */}
        <footer className="mt-16 border-t border-white/5 pt-6">
          <p className="text-[12px] text-neutral-500">
            &copy; {new Date().getFullYear()} {SITE.brandTop} {SITE.brandBottom}. All Rights Reserved.
          </p>
        </footer>
      </main>
    </div>
  );
}

function InfoCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-white/10 bg-navy-900/40 px-5 py-5">
      <div className="w-10 h-10 rounded-lg bg-amber-300/10 border border-amber-300/25 flex items-center justify-center text-amber-300">
        <Icon size={18} strokeWidth={1.6} />
      </div>
      <p className="mt-3 text-[13px] tracking-[0.15em] uppercase text-white font-medium">
        {title}
      </p>
      <p className="mt-2 text-neutral-400 text-[13px] leading-relaxed">{body}</p>
    </div>
  );
}
